import { ApolloClient, createHttpLink, DocumentNode, gql, InMemoryCache, useLazyQuery } from '@apollo/client';
import { JSX, useContext, useEffect, useMemo } from 'react';
import { authLink } from '../common/auth/api';
import { UserContext } from '../common/auth/UserContext';
import { FETCH_POLICY_CONFIG } from '../common/constants/constants';
import { Listener } from '../common/form/FormContext';
import Validator, { createIsNumberRule, ValidatorType } from '../common/form/Validator';
import { useBrowserOrAccountLocale } from '../common/hooks/useBrowserOrAccountLocale';
import { useI18n } from '../common/i18n/I18n';
import { searchClient } from '../common/list/slimQuery';
import { areAllFiltersInList } from '../common/utils/commonUtils';
import { client as termsetClient, termsetListQuery } from '../supply/termset/termset';
import { CUSTOM_FIELD_IMPORT_MAP, fieldTypes, SORTING_FIELDS_FOR_ATTRIBUTES } from './attributes.constants';
import {
    Attribute,
    AvailableType,
    AvailableTypeEnum,
    DateAttribute,
    DecimalField,
    Field,
    FieldDefinitionInput,
    FieldDefinitionWithDescription,
    LocationField,
    MeasurementAttribute,
    MeasurementField,
    NumberAttribute,
    NumberField,
    ReferenceField,
    SelectTypeEnum,
    TermsetAttribute,
    TermsetField,
    TermsetSelectType,
    TextAttribute,
    TextField,
    TimeAttribute,
    Type
} from './attributes.types';
import {
    generateAttributeValidationRules,
    getAttributesByGroups,
    getNonSavedFieldDefinitions,
    getObjectSortedByKeys,
    getSupportedItems,
    parseFieldsToMetadata,
    parseMetadataToFields,
    processAttributes,
    removeSpecialCharactersAndCamelizeKey
} from './attributes.utils';
import { useFieldDefinitionData } from './attributesApi';
import BooleanAttributeEditor from './boolean/BooleanAttributeEditor';
import BooleanAttributePreview from './boolean/BooleanAttributePreview';
import DateAttributeEditor from './date/DateAttributeEditor';
import DateAttributePreview from './date/DateAttributePreview';
import DecimalAttributePreview from './decimal/DecimalAttributePreview';
import DecimalFieldEditor from './decimal/DecimalFieldEditor';
import DecimalFieldPreview from './decimal/DecimalFieldPreview';
import DefaultAttributeEditor from './default/DefaultAttributeEditor';
import DefaultAttributePreview from './default/DefaultAttributePreview';
import DefaultFieldEditor from './default/DefaultFieldEditor';
import DefaultFieldPreview from './default/DefaultFieldPreview';
import DefaultTypePreview from './default/DefaultTypePreview';
import LocationAttributeEditor from './location/LocationAttributeEditor';
import LocationAttributePreview from './location/LocationAttributePreview';
import LocationFieldEditor from './location/LocationFieldEditor';
import MeasurementAttributeEditor from './measurement/MeasurementAttributeEditor';
import MeasurementAttributePreview from './measurement/MeasurementAttributePreview';
import MeasurementFieldEditor from './measurement/MeasurementFieldEditor';
import MeasurementFieldPreview from './measurement/MeasurementFieldPreview';
import NoteAttributeEditor from './note/NoteAttributeEditor';
import NoteAttributePreview from './note/NoteAttributePreview';
import NumberAttributeEditor from './number/NumberAttributeEditor';
import NumberFieldEditor from './number/NumberFieldEditor';
import NumberFieldPreview from './number/NumberFieldPreview';
import NumberTypePreview from './number/NumberTypePreview';
import ReferenceAttributeEditor from './reference/ReferenceAttributeEditor';
import ReferenceAttributePreview from './reference/ReferenceAttributePreview';
import ReferenceFieldEditor from './reference/ReferenceFieldEditor';
import ReferenceFieldPreview from './reference/ReferenceFieldPreview';
import TermsetAttributeEditor from './termset/TermsetAttributeEditor';
import TermsetAttributePreview from './termset/TermsetAttributePreview';
import TermsetFieldEditor from './termset/TermsetFieldEditor';
import TermsetFieldPreview from './termset/TermsetFieldPreview';
import TermsetTypePreview from './termset/TermsetTypePreview';
import TextFieldEditor from './text/TextFieldEditor';
import TextFieldPreview from './text/TextFieldPreview';
import TextTypePreview from './text/TextTypePreview';
import TimeAttributeEditor from './time/TimeAttributeEditor';
import TimeAttributePreview from './time/TimeAttributePreview';

export const client = new ApolloClient({
    link: authLink.concat(
        createHttpLink({
            uri: '/fielddefinition/v1/'
        })
    ),
    cache: new InMemoryCache({
        addTypename: false
    })
});

export const saveFieldDefinition: DocumentNode = gql`
    mutation FieldDefinitions($input: FieldDefinitionInput) {
        saveFieldDefinition(input: $input) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const addFieldDefinitionAssociation: DocumentNode = gql`
    mutation FieldDefinitions($id: ID!, $entity: String!, $entityId: String) {
        addFieldDefinitionAssociation(id: $id, entity: $entity, entityId: $entityId) {
            id
            errors {
                field
                message
            }
        }
    }
`;

export const removeFieldDefinitionAssociation: DocumentNode = gql`
    mutation FieldDefinitions($id: ID!, $entity: String!, $entityId: String) {
        removeFieldDefinitionAssociation(id: $id, entity: $entity, entityId: $entityId) {
            id
            errors {
                field
                message
            }
        }
    }
`;
export const deleteFieldDefinition: DocumentNode = gql`
    mutation FieldDefinitions($id: ID!) {
        deleteFieldDefinition(id: $id) {
            id
            errors {
                field
                key
                message
            }
        }
    }
`;

export const getFieldDefinition: DocumentNode = gql`
    query Preference {
        fieldDefinition {
            name
            fieldType
            label
        }
    }
`;

export const inactivateFieldDefinition: DocumentNode = gql`
    mutation InactivateFieldDefinition($id: ID!) {
        inactivateFieldDefinition(id: $id) {
            id
            errors {
                field
                key
                message
            }
        }
    }
`;

export const activateFieldDefinition: DocumentNode = gql`
    mutation ActivateFieldDefinition($id: ID!) {
        activateFieldDefinition(id: $id) {
            id
            errors {
                field
                key
                message
            }
        }
    }
`;

type ReactElementType = {
    [key in AvailableType]: (props) => JSX.Element;
};

export const typePreview: ReactElementType = {
    [AvailableTypeEnum.number]: NumberTypePreview,
    [AvailableTypeEnum.text]: TextTypePreview,
    [AvailableTypeEnum.termset]: TermsetTypePreview,
    [AvailableTypeEnum.time]: DefaultTypePreview,
    [AvailableTypeEnum.date]: DefaultTypePreview,
    [AvailableTypeEnum.boolean]: DefaultTypePreview,
    [AvailableTypeEnum.decimal]: DefaultTypePreview,
    [AvailableTypeEnum.measurement]: DefaultTypePreview,
    [AvailableTypeEnum.note]: DefaultTypePreview,
    [AvailableTypeEnum.reference]: DefaultTypePreview,
    [AvailableTypeEnum.location]: DefaultTypePreview
};

export const fieldEditors: ReactElementType = {
    [AvailableTypeEnum.number]: NumberFieldEditor,
    [AvailableTypeEnum.text]: TextFieldEditor,
    [AvailableTypeEnum.termset]: TermsetFieldEditor,
    [AvailableTypeEnum.time]: DefaultFieldEditor,
    [AvailableTypeEnum.date]: DefaultFieldEditor,
    [AvailableTypeEnum.boolean]: DefaultFieldEditor,
    [AvailableTypeEnum.decimal]: DecimalFieldEditor,
    [AvailableTypeEnum.measurement]: MeasurementFieldEditor,
    [AvailableTypeEnum.note]: DefaultFieldEditor,
    [AvailableTypeEnum.reference]: ReferenceFieldEditor,
    [AvailableTypeEnum.location]: LocationFieldEditor
};

export const fieldPreview: ReactElementType = {
    [AvailableTypeEnum.text]: TextFieldPreview,
    [AvailableTypeEnum.number]: NumberFieldPreview,
    [AvailableTypeEnum.termset]: TermsetFieldPreview,
    [AvailableTypeEnum.time]: DefaultFieldPreview,
    [AvailableTypeEnum.date]: DefaultFieldPreview,
    [AvailableTypeEnum.boolean]: DefaultFieldPreview,
    [AvailableTypeEnum.decimal]: DecimalFieldPreview,
    [AvailableTypeEnum.measurement]: MeasurementFieldPreview,
    [AvailableTypeEnum.note]: DefaultFieldPreview,
    [AvailableTypeEnum.reference]: ReferenceFieldPreview,
    [AvailableTypeEnum.location]: DefaultFieldPreview
};

export const attributeEditors: ReactElementType = {
    [AvailableTypeEnum.text]: DefaultAttributeEditor,
    [AvailableTypeEnum.number]: NumberAttributeEditor,
    [AvailableTypeEnum.termset]: TermsetAttributeEditor,
    [AvailableTypeEnum.time]: TimeAttributeEditor,
    [AvailableTypeEnum.date]: DateAttributeEditor,
    [AvailableTypeEnum.boolean]: BooleanAttributeEditor,
    [AvailableTypeEnum.decimal]: DefaultAttributeEditor,
    [AvailableTypeEnum.measurement]: MeasurementAttributeEditor,
    [AvailableTypeEnum.note]: NoteAttributeEditor,
    [AvailableTypeEnum.reference]: ReferenceAttributeEditor,
    [AvailableTypeEnum.location]: LocationAttributeEditor
};

export const attributePreview: ReactElementType = {
    [AvailableTypeEnum.text]: DefaultAttributePreview,
    [AvailableTypeEnum.number]: DefaultAttributePreview,
    [AvailableTypeEnum.termset]: TermsetAttributePreview,
    [AvailableTypeEnum.time]: TimeAttributePreview,
    [AvailableTypeEnum.date]: DateAttributePreview,
    [AvailableTypeEnum.boolean]: BooleanAttributePreview,
    [AvailableTypeEnum.decimal]: DecimalAttributePreview,
    [AvailableTypeEnum.measurement]: MeasurementAttributePreview,
    [AvailableTypeEnum.note]: NoteAttributePreview,
    [AvailableTypeEnum.reference]: ReferenceAttributePreview,
    [AvailableTypeEnum.location]: LocationAttributePreview
};

export const useTypeValidationRules = (selectedType, value) => {
    const { activeOrganizationAccount } = useContext(UserContext);
    const { fieldDefinitionData } = useFieldDefinitionData(undefined, 'active');
    const locale = useBrowserOrAccountLocale();

    const [getData, { data: termsetsData }] = useLazyQuery(termsetListQuery, {
        client: termsetClient,
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        },
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
        notifyOnNetworkStatusChange: false
    });

    useEffect(() => {
        if (selectedType?.fieldType === AvailableTypeEnum.termset) {
            getData();
        }
    }, [selectedType, getData]);

    /* Validation rules for Types */
    type TypeValidationRulesType = {
        [key in AvailableType]: {
            [field: string]: ValidatorType[];
        };
    };
    const termsetValidationMsg = useI18n('termset.fieldDefinition.validationMsg');
    const labelValidationMsg = useI18n('fieldDefinition.labelValidationMsg');
    const minValidationMsg = useI18n('fieldDefinition.minValidation');
    const maxValidationMsg = useI18n('fieldDefinition.maxValidation');

    const typeValidationRules = useMemo(() => {
        const existingFieldDefByGroup = fieldDefinitionData?.results?.hits?.items
            ? fieldDefinitionData.results.hits.items.reduce((accumulator, currentVal) => {
                  const name = currentVal.name.toLowerCase().replace(/\s+/g, '');
                  const group = currentVal.group.toLowerCase().replace(/\s+/g, '');
                  if (accumulator[group]) {
                      accumulator[group].push(name);
                  } else {
                      accumulator[group] = [name];
                  }
                  return accumulator;
              }, {})
            : [];

        const commonLabelValidator: ValidatorType = {
            isValid: (prop, state, isModified) => {
                const group = state.group.toLowerCase().replace(/\s+/g, '');
                const label = state.label.toLowerCase().replace(/\s+/g, '');
                return existingFieldDefByGroup?.[group] ? !existingFieldDefByGroup[group].includes(label) : true;
            },
            messageKey: labelValidationMsg
        };

        const minMaxValidator: ValidatorType = {
            isValid: (prop, state, isModified) => {
                return !(parseFloat(state.min) > parseFloat(state.max));
            }
        };
        const commonMaxValidator: ValidatorType = { ...minMaxValidator, messageKey: maxValidationMsg };
        const commonMinValidator: ValidatorType = { ...minMaxValidator, messageKey: minValidationMsg };

        const staticRules: TypeValidationRulesType = {
            [AvailableTypeEnum.text]: {
                label: [Validator.RULES.isRequired, commonLabelValidator]
            },
            [AvailableTypeEnum.number]: {
                label: [Validator.RULES.isRequired, commonLabelValidator],
                min: [createIsNumberRule(locale), commonMinValidator],
                max: [createIsNumberRule(locale), commonMaxValidator]
            },
            [AvailableTypeEnum.termset]: {
                label: [Validator.RULES.isRequired, commonLabelValidator],
                termset: !value?.newTermset?.length ? [Validator.RULES.isRequired] : [],
                newTermset: !value?.termset?.length ? [Validator.RULES.isRequired] : []
            },
            [AvailableTypeEnum.time]: {
                label: [Validator.RULES.isRequired, commonLabelValidator]
            },
            [AvailableTypeEnum.date]: {
                label: [Validator.RULES.isRequired, commonLabelValidator]
            },
            [AvailableTypeEnum.boolean]: {
                label: [Validator.RULES.isRequired, commonLabelValidator]
            },
            [AvailableTypeEnum.decimal]: {
                label: [Validator.RULES.isRequired, commonLabelValidator],
                min: [createIsNumberRule(locale), commonMinValidator],
                max: [createIsNumberRule(locale), commonMaxValidator],
                decimalsNumber: [createIsNumberRule(locale)]
            },
            [AvailableTypeEnum.measurement]: {
                label: [Validator.RULES.isRequired, commonLabelValidator],
                min: [createIsNumberRule(locale), commonMinValidator],
                max: [createIsNumberRule(locale), commonMaxValidator],
                decimalsNumber: [createIsNumberRule(locale)],
                measurementType: [Validator.RULES.isRequired],
                unit: [Validator.RULES.isRequired]
            },
            [AvailableTypeEnum.reference]: {
                label: [Validator.RULES.isRequired, commonLabelValidator],
                entityToReference: [Validator.RULES.isRequired]
            },
            [AvailableTypeEnum.location]: {
                label: [Validator.RULES.isRequired, commonLabelValidator]
            },
            [AvailableTypeEnum.note]: {
                label: [Validator.RULES.isRequired, commonLabelValidator]
            }
        };

        if (termsetsData?.termsets && selectedType?.fieldType === AvailableTypeEnum.termset) {
            staticRules.termset.label.push({
                isValid: (prop, state, isModified) => {
                    const termsetLabels = termsetsData.termsets.map((termset) => termset.label);
                    return !state.newTermset.length || !termsetLabels.includes(state.label);
                },
                messageKey: termsetValidationMsg
            });
        }
        return selectedType?.fieldType ? staticRules[selectedType.fieldType] : {};
    }, [
        fieldDefinitionData?.results?.hits?.items,
        labelValidationMsg,
        locale,
        maxValidationMsg,
        minValidationMsg,
        selectedType?.fieldType,
        termsetValidationMsg,
        termsetsData?.termsets,
        value?.newTermset?.length,
        value?.termset?.length
    ]);

    return { typeValidationRules };
};

/* Listeners for Types */
type TypeListenersType = {
    [key in AvailableType]: Listener[];
};
export const typeListeners: TypeListenersType = {
    [AvailableTypeEnum.text]: [
        {
            prop: 'label',
            func: (name, newValue, newState: Field) => {
                newState.name = removeSpecialCharactersAndCamelizeKey(newValue);
            }
        }
    ],
    [AvailableTypeEnum.number]: [
        {
            prop: 'label',
            func: (name, newValue, newState: Field) => {
                newState.name = removeSpecialCharactersAndCamelizeKey(newValue);
            }
        }
    ],
    [AvailableTypeEnum.termset]: [
        {
            prop: 'label',
            func: (name, newValue, newState: Field) => {
                newState.name = removeSpecialCharactersAndCamelizeKey(newValue);
            }
        },
        {
            prop: 'newTermset',
            func: (name, newValue, newState: TermsetField) => {
                newState.termset = '';
            }
        },
        {
            prop: 'termset',
            func: (name, newValue, newState: TermsetField) => {
                newState.newTermset = [];
            }
        },
        {
            prop: 'selectType',
            func: (name, newValue, newState: TermsetField) => {
                if (newValue.id === SelectTypeEnum.multi) {
                    newState.isMultiSelect = true;
                } else {
                    newState.isMultiSelect = false;
                }
            }
        }
    ],
    [AvailableTypeEnum.time]: [
        {
            prop: 'label',
            func: (name, newValue, newState: Field) => {
                newState.name = removeSpecialCharactersAndCamelizeKey(newValue);
            }
        }
    ],
    [AvailableTypeEnum.date]: [
        {
            prop: 'label',
            func: (name, newValue, newState: Field) => {
                newState.name = removeSpecialCharactersAndCamelizeKey(newValue);
            }
        }
    ],
    [AvailableTypeEnum.boolean]: [
        {
            prop: 'label',
            func: (name, newValue, newState: Field) => {
                newState.name = removeSpecialCharactersAndCamelizeKey(newValue);
            }
        }
    ],
    [AvailableTypeEnum.decimal]: [
        {
            prop: 'label',
            func: (name, newValue, newState: Field) => {
                newState.name = removeSpecialCharactersAndCamelizeKey(newValue);
            }
        }
    ],
    [AvailableTypeEnum.measurement]: [
        {
            prop: 'label',
            func: (name, newValue, newState: Field) => {
                newState.name = removeSpecialCharactersAndCamelizeKey(newValue);
            }
        }
    ],
    [AvailableTypeEnum.note]: [
        {
            prop: 'label',
            func: (name, newValue, newState: Field) => {
                newState.name = removeSpecialCharactersAndCamelizeKey(newValue);
            }
        }
    ],
    [AvailableTypeEnum.reference]: [
        {
            prop: 'label',
            func: (name, newValue, newState: Field) => {
                newState.name = removeSpecialCharactersAndCamelizeKey(newValue);
            }
        },
        {
            prop: 'selectType',
            func: (name, newValue, newState: TermsetField) => {
                if (newValue.id === SelectTypeEnum.multi) {
                    newState.isMultiSelect = true;
                } else {
                    newState.isMultiSelect = false;
                }
            }
        }
    ],
    [AvailableTypeEnum.location]: [
        {
            prop: 'label',
            func: (name, newValue, newState: Field) => {
                newState.name = removeSpecialCharactersAndCamelizeKey(newValue);
            }
        },
        {
            prop: 'selectType',
            func: (name, newValue, newState) => {
                if (newValue.id === SelectTypeEnum.multi) {
                    newState.isMultiSelect = true;
                } else {
                    newState.isMultiSelect = false;
                }
            }
        }
    ]
};

export const arrayToSelectOptionsLocalized = (arr) => {
    return arr && arr.length > 0
        ? arr.map((item) => {
              return { id: item, label: useI18n(`termset.selectType.${item}`) };
          })
        : [];
};

export const arrayToSelectLocationsLocalized = (arr) => {
    return arr && !!arr.length
        ? arr.map((item) => {
              return { id: item, label: useI18n(`attributes.location.${item}`) };
          })
        : [];
};

export const refetchFieldDefinitions = () =>
    searchClient.refetchQueries({
        include: ['slimResultsQuery'],
        onQueryUpdated(observableQuery) {
            return areAllFiltersInList(observableQuery.options.variables.filters, [
                {
                    identifier: 'entity',
                    value: 'Activity'
                },
                {
                    identifier: 'entityType',
                    value: 'Resource'
                }
            ]);
        }
    });

// Re-export types and enums from utils for backward compatibility
export {
    AvailableTypeEnum,
    CUSTOM_FIELD_IMPORT_MAP,
    fieldTypes,
    generateAttributeValidationRules,
    getAttributesByGroups,
    getNonSavedFieldDefinitions,
    getObjectSortedByKeys,
    getSupportedItems,
    parseFieldsToMetadata,
    parseMetadataToFields,
    processAttributes,
    removeSpecialCharactersAndCamelizeKey,
    SelectTypeEnum,
    SORTING_FIELDS_FOR_ATTRIBUTES
};
export type {
    Attribute,
    AvailableType,
    DateAttribute,
    DecimalField,
    Field,
    FieldDefinitionInput,
    FieldDefinitionWithDescription,
    LocationField,
    MeasurementAttribute,
    MeasurementField,
    NumberAttribute,
    NumberField,
    ReferenceField,
    TermsetAttribute,
    TermsetField,
    TermsetSelectType,
    TextAttribute,
    TextField,
    TimeAttribute,
    Type
};
